import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import { LuChevronDown as ChevronDown, LuChevronUp as ChevronUp, LuX as X } from "react-icons/lu";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { findTextMatchesInDoc } from "../../utils/findTextMatchesInDoc";

type FindReplaceBarProps = {
  editor: Editor | null;
  open: boolean;
  onClose: () => void;
};

export function FindReplaceBar({ editor, open, onClose }: FindReplaceBarProps) {
  const findInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [findQuery, setFindQuery] = useState("");
  const [replaceQuery, setReplaceQuery] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
  const [docVersion, setDocVersion] = useState(0);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      findInputRef.current?.focus();
      findInputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open || !editor) return;
    const { from, to } = editor.state.selection;
    if (from !== to && !findQuery.trim()) {
      const selected = editor.state.doc.textBetween(from, to, "\n", "\n").trim();
      if (selected) setFindQuery(selected);
    }
  }, [open, editor]); // eslint-disable-line react-hooks/exhaustive-deps -- seed once when opening

  useEffect(() => {
    if (!editor) return;
    const onUpdate = () => setDocVersion((v) => v + 1);
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    if (!open || !findQuery.trim()) {
      editor.commands.clearSearch();
      return;
    }
    editor.commands.setSearchCaseSensitive(matchCase);
    editor.commands.setSearchTerm(findQuery);
  }, [editor, open, findQuery, matchCase]);

  useEffect(() => {
    if (!editor || !open || !findQuery.trim() || activeMatchIndex < 0) return;
    editor.commands.setSearchCurrentMatch(activeMatchIndex);
  }, [editor, open, findQuery, activeMatchIndex]);

  useEffect(() => {
    if (!open) {
      editor?.commands.clearSearch();
      setHint(null);
    }
  }, [open, editor]);

  const matches = useMemo(() => {
    if (!editor || !findQuery.length) return [];
    return findTextMatchesInDoc(editor.state.doc, findQuery, matchCase);
  }, [editor, docVersion, findQuery, matchCase]);

  const jumpToMatch = useCallback(
    (nextIndex: number) => {
      if (!editor || matches.length === 0) return;
      const len = matches.length;
      const normalized = ((nextIndex % len) + len) % len;
      const m = matches[normalized];
      try {
        const tr = editor.state.tr.setSelection(TextSelection.create(editor.state.doc, m.from, m.to));
        editor.view.dispatch(tr);
        editor.commands.setSearchCurrentMatch(normalized);
        const dom = editor.view.domAtPos(m.from);
        const el = dom.node instanceof Element ? dom.node : dom.node.parentElement;
        el?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      } catch {
        /* ignore invalid selection */
      }
      setActiveMatchIndex(normalized);
      setHint(null);
    },
    [editor, matches]
  );

  const jumpToNext = useCallback(() => {
    if (!matches.length) {
      setHint("No matches found.");
      return;
    }
    jumpToMatch(activeMatchIndex + 1);
  }, [matches.length, jumpToMatch, activeMatchIndex]);

  const jumpToPrev = useCallback(() => {
    if (!matches.length) {
      setHint("No matches found.");
      return;
    }
    const fallback = activeMatchIndex >= 0 ? activeMatchIndex : 0;
    jumpToMatch(fallback - 1);
  }, [matches.length, jumpToMatch, activeMatchIndex]);

  const replaceCurrent = useCallback(() => {
    if (!editor) return;
    if (activeMatchIndex < 0 || activeMatchIndex >= matches.length) {
      setHint("Choose a match first.");
      return;
    }
    const current = matches[activeMatchIndex];
    const ok = editor
      .chain()
      .focus()
      .insertContentAt({ from: current.from, to: current.to }, replaceQuery)
      .run();
    if (!ok) {
      setHint("Could not replace this match.");
      return;
    }
    const fresh = findTextMatchesInDoc(editor.state.doc, findQuery, matchCase);
    if (!fresh.length) {
      setActiveMatchIndex(-1);
      setHint("Replaced the last match.");
      return;
    }
    const nextIndex = Math.min(activeMatchIndex, fresh.length - 1);
    setActiveMatchIndex(nextIndex);
    const next = fresh[nextIndex];
    try {
      editor.chain().focus().setTextSelection({ from: next.from, to: next.to }).run();
    } catch {
      /* ignore */
    }
    setHint("Replaced 1 match.");
  }, [editor, activeMatchIndex, matches, replaceQuery, findQuery, matchCase]);

  const replaceAll = useCallback(() => {
    if (!editor) return;
    if (!findQuery.length) {
      setHint("Enter text to find first.");
      return;
    }
    const all = findTextMatchesInDoc(editor.state.doc, findQuery, matchCase);
    if (!all.length) {
      setHint("No matches found.");
      return;
    }
    let tr = editor.state.tr;
    for (let i = all.length - 1; i >= 0; i--) {
      const m = all[i]!;
      tr = tr.insertText(replaceQuery, m.from, m.to);
    }
    editor.view.dispatch(tr);
    editor.commands.focus();
    setActiveMatchIndex(-1);
    setHint(`Replaced ${all.length} ${all.length === 1 ? "match" : "matches"}.`);
  }, [editor, findQuery, matchCase, replaceQuery]);

  const handleClose = useCallback(() => {
    editor?.commands.clearSearch();
    setHint(null);
    onClose();
  }, [editor, onClose]);

  if (!open) return null;

  const matchLabel =
    !findQuery.length
      ? ""
      : matches.length === 0
        ? "No matches"
        : `${Math.max(1, activeMatchIndex + 1)} of ${matches.length}`;

  return (
    <section
      className="flex shrink-0 flex-col gap-1.5 border-b border-border bg-muted/50 px-3 py-2"
      role="search"
      aria-label="Find and replace"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          handleClose();
        } else if (e.key === "Enter" && e.target === findInputRef.current) {
          e.preventDefault();
          if (e.shiftKey) jumpToPrev();
          else jumpToNext();
        }
      }}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Input
          ref={findInputRef}
          value={findQuery}
          placeholder="Find"
          className="h-7 min-w-32 flex-[1_1_8rem] sm:max-w-48"
          aria-label="Find"
          onChange={(e) => {
            setFindQuery(e.target.value);
            setActiveMatchIndex(-1);
            setHint(null);
          }}
        />
        <Input
          ref={replaceInputRef}
          value={replaceQuery}
          placeholder="Replace"
          className="h-7 min-w-32 flex-[1_1_8rem] sm:max-w-48"
          aria-label="Replace"
          onChange={(e) => setReplaceQuery(e.target.value)}
        />
        <span className="min-w-18 text-xs text-muted-foreground" aria-live="polite">
          {matchLabel}
        </span>
        <div className="inline-flex items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Previous match"
            disabled={!matches.length}
            onClick={jumpToPrev}
          >
            <ChevronUp className="size-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Next match"
            disabled={!matches.length}
            onClick={jumpToNext}
          >
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={!matches.length} onClick={replaceCurrent}>
          Replace
        </Button>
        <Button type="button" size="sm" disabled={!matches.length} onClick={replaceAll}>
          Replace all
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="aria-pressed:bg-primary/15 aria-pressed:text-primary"
          aria-pressed={matchCase}
          aria-label="Match case"
          title="Match case"
          onClick={() => {
            setMatchCase((v) => !v);
            setHint(null);
          }}
        >
          Aa
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Close find and replace"
          onClick={handleClose}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      {hint ? (
        <p className="text-xs text-muted-foreground" role="status">
          {hint}
        </p>
      ) : null}
    </section>
  );
}
